import { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { MapPin, Loader2 } from 'lucide-react';
import { searchAddresses } from '../utils/geocoding';

const AddressAutocomplete = ({ value, onChange, placeholder, className, icon: Icon = MapPin, required = false }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    // Click outside handler
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = async (inputValue) => {
    onChange(inputValue);
    setSelectedIndex(-1);

    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Si moins de 3 caractères, pas de suggestions mais on garde la valeur
    if (!inputValue || inputValue.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce API call pour les suggestions (optionnelles)
    debounceTimerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchAddresses(inputValue);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch (error) {
        console.error('Address search error:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms debounce
  };

  const handleSelectSuggestion = (suggestion) => {
    onChange(suggestion.formatted);
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Icon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        <Input
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || "Ex: 11 avenue des Grenadiers, 30132 Caissargues"}
          className={`pl-10 pr-10 ${className}`}
          required={required}
          autoComplete="off"
          title="Vous pouvez taper librement votre adresse ou sélectionner une suggestion"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-3 h-4 w-4 text-gray-400 animate-spin" />
        )}
      </div>
      
      {/* Aide pour l'utilisateur */}
      {!showSuggestions && value.length === 0 && (
        <p className="text-xs text-gray-500 mt-1">
          💡 Saisissez votre adresse complète : numéro, rue, code postal, ville
        </p>
      )}
      
      {!showSuggestions && value.length > 0 && value.length < 3 && (
        <p className="text-xs text-blue-600 mt-1">
          ✓ Vous pouvez continuer à taper librement
        </p>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <Card className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto shadow-lg">
          <div className="p-1">
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                onClick={() => handleSelectSuggestion(suggestion)}
                className={`flex items-start gap-2 p-3 cursor-pointer rounded-md transition-colors ${
                  index === selectedIndex
                    ? 'bg-purple-100 text-purple-900'
                    : 'hover:bg-gray-100'
                }`}
              >
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-purple-600" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {suggestion.formatted}
                  </div>
                  {suggestion.display_name !== suggestion.formatted && (
                    <div className="text-xs text-gray-500 truncate">
                      {suggestion.display_name}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {showSuggestions && !loading && suggestions.length === 0 && value.length >= 3 && (
        <Card className="absolute z-50 w-full mt-1 shadow-lg">
          <div className="p-3 text-sm text-gray-500 text-center">
            Aucune adresse trouvée
          </div>
        </Card>
      )}
    </div>
  );
};

export default AddressAutocomplete;
